import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import './InvoicePreview.css';
import './InvoicePreview.theme.css';
import EditedPageRaster from './EditedPageRaster';
import { alignEditorInkToField } from './pdfTextAlignment';
import { exactPdfFontPresentation, ensureExactPdfFont, exactMatriceBitsFontReady, useEmbeddedPdfFonts } from './pdfFontCompat';
import FontPicker from './FontPicker';


function number(value, fallback = 0) {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : fallback;
}

function resolveEditValue(originalValue, localExists, localValue, parentExists, parentValue) {
  const original = String(originalValue ?? '');
  if (localExists) {
    const value = String(localValue ?? '');
    return { value, hasEdit: value !== original };
  }
  if (parentExists) {
    const value = String(parentValue ?? '');
    return { value, hasEdit: value !== original };
  }
  return { value: original, hasEdit: false };
}

function getCharGeometry(char, pageHeight) {
  const p = char?.position || {};
  const s = char?.size || {};
  const x = number(p.bbox_x ?? p.x);
  const top = Number.isFinite(Number(p.bbox_y))
    ? number(p.bbox_y)
    : pageHeight - number(p.y, pageHeight) - number(s.height, char?.font?.size || 12);
  const width = Math.max(number(p.bbox_right, x + number(s.width, char?.font?.size || 12)) - x, 0.01);
  const height = Math.max(number(p.bbox_bottom, top + number(s.height, char?.font?.size || 12)) - top, 0.01);
  return { x, top, width, height };
}

function charsForField(page, field) {
  const pageChars = Array.isArray(page?.characters) ? page.characters : [];
  const refs = [
    ...(field?.source?.character_global_indexes || []),
    ...(field?.source_character_indexes || []),
    ...(field?.character_indexes || []),
  ].map(Number).filter((value) => Number.isInteger(value) && value >= 0 && value < pageChars.length);

  if (refs.length) {
    return Array.from(new Set(refs)).map((indexValue, index) => ({
      ...pageChars[indexValue],
      __index: index,
      __box: getCharGeometry(pageChars[indexValue], number(page?.dimensions?.height, 842)),
    }));
  }

  const sourceChars = Array.isArray(field?.source_characters)
    ? field.source_characters
    : Array.isArray(field?.characters)
      ? field.characters
      : [];

  if (sourceChars.length) {
    return sourceChars.map((sourceChar, index) => {
      const globalIndex = Number(sourceChar?.global_char_index);
      const resolved = Number.isInteger(globalIndex) && pageChars[globalIndex] ? pageChars[globalIndex] : sourceChar;
      return {
        ...(resolved || sourceChar),
        __index: index,
        __box: getCharGeometry(resolved || sourceChar, number(page?.dimensions?.height, 842)),
      };
    });
  }

  const bbox = field?.bbox || {};
  const x0 = number(bbox.x0);
  const y0 = number(bbox.y0);
  const x1 = number(bbox.x1, x0 + number(bbox.width));
  const y1 = number(bbox.y1, y0 + number(bbox.height));
  if (x1 <= x0 || y1 <= y0) return [];

  return pageChars.map((char, index) => ({
    ...char,
    __index: index,
    __box: getCharGeometry(char, number(page?.dimensions?.height, 842)),
  })).filter((char) => {
    const box = char.__box;
    return Math.min(box.x + box.width, x1) > Math.max(box.x, x0)
      && Math.min(box.top + box.height, y1) > Math.max(box.top, y0);
  });
}

function splitLegacySpan(chars, prefix) {
  const sorted = [...chars].sort((a, b) => number(a?.__box?.x) - number(b?.__box?.x) || Number(a.__index) - Number(b.__index));
  const parts = [];
  let current = [];
  const flush = () => {
    if (!current.length) return;
    const visible = current.filter((char) => String(char?.text ?? '').trim());
    if (!visible.length) { current = []; return; }
    const boxes = current.map((char) => char.__box);
    const left = Math.min(...boxes.map((box) => box.x));
    const top = Math.min(...boxes.map((box) => box.top));
    const right = Math.max(...boxes.map((box) => box.x + box.width));
    const bottom = Math.max(...boxes.map((box) => box.top + box.height));
    const first = visible[0];
    parts.push({ id: `${prefix}:part:${parts.length}`, chars: [...current], text: current.map((char) => char?.text ?? '').join('').trim(), x: left, top, width: Math.max(1, right - left), height: Math.max(1, bottom - top), font: first?.font || {}, font_lock: null, color: first?.color || '#000000' });
    current = [];
  };
  for (let i = 0; i < sorted.length; i += 1) {
    const char = sorted[i];
    const prev = sorted[i - 1];
    const fontSize = Number(char?.font?.size || prev?.font?.size || 12);
    if (current.length && prev) {
      const gap = char.__box.x - (prev.__box.x + prev.__box.width);
      const prevSpace = !String(prev?.text ?? '').trim();
      const curSpace = !String(char?.text ?? '').trim();
      const pf = prev?.font || {}; const cf = char?.font || {};
      const same = Number.isFinite(Number(pf.xref)) && Number.isFinite(Number(cf.xref)) ? Number(pf.xref) === Number(cf.xref) : (!pf.css_family || !cf.css_family || pf.css_family === cf.css_family);
      if (!same || (gap > Math.max(6, fontSize * 1.15) && !prevSpace && !curSpace)) flush();
    }
    current.push(char);
    if (String(char?.text ?? '').trim()) {
      let whitespaceWidth = 0; let j = i + 1;
      while (j < sorted.length && !String(sorted[j]?.text ?? '').trim()) { whitespaceWidth += sorted[j].__box.width; j += 1; }
      if (j < sorted.length && whitespaceWidth > Math.max(6, fontSize * 1.1)) flush();
    }
  }
  flush();
  return parts.filter((part) => part.text.length > 0);
}

function buildLegacyLines(page) {
  const spanMap = new Map();
  (page?.characters || []).forEach((char, index) => {
    const key = `${char?.block ?? 0}:${char?.line ?? 0}:${char?.span ?? index}`;
    if (!spanMap.has(key)) spanMap.set(key, []);
    spanMap.get(key).push({
      ...char,
      __index: index,
      __box: getCharGeometry(char, number(page?.dimensions?.height, 842)),
    });
  });
  const fields = [];
  for (const [key, chars] of spanMap.entries()) fields.push(...splitLegacySpan(chars, `legacy_${key}`));
  return fields;
}

function buildEditableFields(page) {
  if (Array.isArray(page?.fields) && page.fields.length) {
    return page.fields.map((field, index) => {
      const bbox = field?.bbox || {};
      const chars = charsForField(page, field);
      const first = chars.find((char) => String(char?.text || '').trim());
      const font = Object.keys(field?.font || {}).length ? field.font : (first?.font || {});
      const lock = Object.keys(field?.font_lock || {}).length ? field.font_lock : font;
      return {
        id: String(field?.id || `field_${index}`),
        text: String(field?.text ?? chars.map((char) => char?.text ?? '').join('')),
        x: number(bbox.x0 ?? field?.position?.x),
        top: number(bbox.y0),
        width: Math.max(1, number(bbox.width, number(bbox.x1) - number(bbox.x0))),
        height: Math.max(1, number(bbox.height, number(bbox.y1) - number(bbox.y0))),
        font,
        font_lock: lock,
        color: field?.color || first?.color || '#000000',
        chars,
        rawField: field,
      };
    });
  }
  return buildLegacyLines(page);
}

function measureTextWidth(text, presentation, fontSizePx) {
  const canvas = measureTextWidth.canvas || (measureTextWidth.canvas = document.createElement('canvas'));
  const context = canvas.getContext('2d');
  if (!context) return String(text || '').length * fontSizePx * 0.55;
  context.font = `${presentation.style} ${presentation.weight} ${fontSizePx}px ${presentation.family}`;
  if ('fontKerning' in context) context.fontKerning = 'normal';
  return context.measureText(String(text || '')).width;
}

function placeCaretAtPoint(element, clientX, clientY) {
  if (!element) return false;
  try {
    const doc = element.ownerDocument;
    let range = null;
    if (typeof doc.caretRangeFromPoint === 'function') {
      range = doc.caretRangeFromPoint(clientX, clientY);
    } else if (typeof doc.caretPositionFromPoint === 'function') {
      const position = doc.caretPositionFromPoint(clientX, clientY);
      if (position) {
        range = doc.createRange();
        range.setStart(position.offsetNode, position.offset);
        range.collapse(true);
      }
    }
    if (!range || !element.contains(range.startContainer)) return false;
    const selection = doc.getSelection();
    selection.removeAllRanges();
    selection.addRange(range);
    return true;
  } catch {
    return false;
  }
}

function textOffsetFromSelection(element) {
  const selection = element.ownerDocument?.getSelection?.();
  if (!selection || !selection.rangeCount || !element.contains(selection.anchorNode)) return null;
  const range = selection.getRangeAt(0).cloneRange();
  const before = range.cloneRange();
  before.selectNodeContents(element);
  before.setEnd(selection.anchorNode, selection.anchorOffset);
  return before.toString().length;
}

function restoreTextOffset(element, offset) {
  if (!element || !Number.isFinite(offset)) return false;
  const doc = element.ownerDocument;
  const walker = doc.createTreeWalker(element, NodeFilter.SHOW_TEXT);
  let remaining = offset;
  let node = walker.nextNode();
  while (node) {
    const length = node.nodeValue?.length || 0;
    if (remaining <= length) {
      const range = doc.createRange();
      range.setStart(node, remaining);
      range.collapse(true);
      const selection = doc.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
      return true;
    }
    remaining -= length;
    node = walker.nextNode();
  }
  const range = doc.createRange();
  range.selectNodeContents(element);
  range.collapse(false);
  const selection = doc.getSelection();
  selection.removeAllRanges();
  selection.addRange(range);
  return true;
}

function renderDrawingItems(drawings, W, H) {
  if (!Array.isArray(drawings) || drawings.length === 0) return null;
  return <svg className="invoice-vector-drawings" viewBox={`0 0 ${W} ${H}`} aria-hidden="true">
    {drawings.flatMap((drawing) => (drawing.items || []).map((item, index) => {
      const common = { key: `drawing-${drawing.id}-${index}`, stroke: drawing.stroke || 'none', fill: drawing.fill || 'none', strokeWidth: drawing.width || 0, strokeOpacity: drawing.stroke_opacity ?? undefined, fillOpacity: drawing.fill_opacity ?? undefined, vectorEffect: 'non-scaling-stroke' };
      if (item.type === 'line') return <line {...common} x1={item.x1} y1={item.y1} x2={item.x2} y2={item.y2} />;
      if (item.type === 'rect') return <rect {...common} x={item.x} y={item.y} width={item.width} height={item.height} />;
      if (item.type === 'quad') return <polygon {...common} points={(item.points || []).map((point) => `${point.x},${point.y}`).join(' ')} />;
      if (item.type === 'curve' && (item.points || []).length === 4) { const p = item.points; return <path {...common} d={`M ${p[0].x} ${p[0].y} C ${p[1].x} ${p[1].y}, ${p[2].x} ${p[2].y}, ${p[3].x} ${p[3].y}`} />; }
      return null;
    }))}
  </svg>;
}

export default function InvoicePreview({ template, formData = {}, onTextChange }) {
  const fontRevision = useEmbeddedPdfFonts(template);
  const [zoom, setZoom] = useState(1);
  const [editingKey, setEditingKey] = useState(null);
  const [localEdits, setLocalEdits] = useState({});
  const [selectedFonts, setSelectedFonts] = useState({});
  const fieldRefs = useRef(new Map());
  const pageRefs = useRef(new Map());
  const pendingCaret = useRef(null);
  const pages = template?.pages || [];
  const pageModels = useMemo(() => pages.map((page, pageIndex) => ({ page, pageIndex, fields: buildEditableFields(page) })), [pages]);

  useEffect(() => { setLocalEdits({}); setEditingKey(null); setSelectedFonts({}); pendingCaret.current = null; }, [template]);

  useLayoutEffect(() => {
    if (!editingKey) return;
    const element = fieldRefs.current.get(editingKey);
    if (!element) return;
    element.focus({ preventScroll: true });
    const pending = pendingCaret.current;
    if (pending?.key === editingKey) {
      let placed = false;
      if (Number.isFinite(pending.offset)) placed = restoreTextOffset(element, pending.offset);
      else if (Number.isFinite(pending.clientX) && Number.isFinite(pending.clientY)) placed = placeCaretAtPoint(element, pending.clientX, pending.clientY);
      if (!placed) restoreTextOffset(element, element.textContent?.length || 0);
    }
  }, [editingKey]);

  useLayoutEffect(() => {
    for (const { pageIndex, fields } of pageModels) {
      for (const field of fields) {
        const key = `${pageIndex}:${field.id}`;
        const element = fieldRefs.current.get(key);
        if (!element || editingKey === key) continue;
        const hasLocalEdit = Object.prototype.hasOwnProperty.call(localEdits, key);
        const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
        const resolved = resolveEditValue(field.text, hasLocalEdit, localEdits[key], hasParentEdit, formData[key]);
        const desired = resolved.hasEdit ? resolved.value : '';
        if (element.textContent !== desired) element.textContent = desired;
      }
    }
  }, [pageModels, localEdits, formData, editingKey]);

  useLayoutEffect(() => {
    for (const { pageIndex, page, fields } of pageModels) {
      const pageElement = pageRefs.current.get(pageIndex);
      if (!pageElement) continue;
      const scale = zoom;
      for (const field of fields) {
        const key = `${pageIndex}:${field.id}`;
        const element = fieldRefs.current.get(key);
        if (!element) continue;
        const hasLocalEdit = Object.prototype.hasOwnProperty.call(localEdits, key);
        const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
        const resolved = resolveEditValue(field.text, hasLocalEdit, localEdits[key], hasParentEdit, formData[key]);
        if (!resolved.value) continue;
        alignEditorInkToField({ element, pageElement, field, scale });
      }
    }
  }, [pageModels, localEdits, formData, zoom, editingKey]);

  const beginEdit = async (pageIndex, field, event) => {
    event.preventDefault();
    event.stopPropagation();

    const key = `${pageIndex}:${field.id}`;
    const element = fieldRefs.current.get(key);

    // IMPORTANT:
    // Before switching contentEditable on, restore the real field text in the
    // DOM. When a field is not being edited its editor layer is intentionally
    // empty (the PDF/raster remains visible underneath). Without restoring the
    // text here, the first keypress edits an empty contentEditable and appears
    // to replace the entire original field.
    const hasLocalEdit = Object.prototype.hasOwnProperty.call(localEdits, key);
    const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
    const resolved = resolveEditValue(
      field.text,
      hasLocalEdit,
      localEdits[key],
      hasParentEdit,
      formData[key]
    );

    if (element) {
      element.textContent = resolved.value;
    }

    pendingCaret.current = {
      key,
      clientX: event.clientX,
      clientY: event.clientY,
      offset: null
    };

    // Never expose the replacement text before the field font is ready.
    await ensureExactPdfFont(template, field, selectedFonts[key] || null);

    if (fieldRefs.current.get(key)) {
      setEditingKey(key);
    }
  };

  const liveEdit = (pageIndex, field, value, element) => {
    const key = `${pageIndex}:${field.id}`;
    const finalValue = String(value ?? '');
    const offset = element ? textOffsetFromSelection(element) : null;
    pendingCaret.current = { ...(pendingCaret.current || {}), key, offset, clientX: null, clientY: null };
    setLocalEdits((previous) => ({ ...previous, [key]: finalValue }));
    onTextChange?.({ key, pageIndex, fieldId: field.id, lineId: field.id, text: finalValue });
  };

  if (!template || pages.length === 0) return <div className="invoice-preview-empty">Aucun template de facture chargé.</div>;

  return (
    <div className="invoice-preview" data-font-revision={fontRevision}>
      <div className="invoice-preview-toolbar">
        <div className="invoice-zoom-controls">
          <button type="button" onClick={() => setZoom((value) => Math.min(5, Math.max(0.5, Number((value - 0.1).toFixed(2)))))} aria-label="Réduire le zoom">−</button>
          <button type="button" className="invoice-zoom-value" onClick={() => setZoom(1)}>{Math.round(zoom * 100)}%</button>
          <button type="button" onClick={() => setZoom((value) => Math.min(5, Math.max(0.5, Number((value + 0.1).toFixed(2)))))} aria-label="Augmenter le zoom">+</button>
          <button type="button" className="invoice-zoom-reset" onClick={() => setZoom(1)}>Réinitialiser</button>
        </div>
        <div className="invoice-zoom-slider-wrap">
          <span>50%</span>
          <input className="invoice-zoom-slider" type="range" min="0.5" max="5" step="0.05" value={zoom} onChange={(event) => setZoom(Number(event.target.value))} aria-label="Zoom" />
          <span>500%</span>
        </div>
      </div>

      <div className="invoice-preview-scroll">
        <div className="invoice-pages" style={{ minWidth: 'max-content' }}>
          {pageModels.map(({ page, pageIndex, fields }) => {
            const W = number(page?.dimensions?.width, 595);
            const H = number(page?.dimensions?.height, 842);
            const scaledWidth = W * zoom;
            const scaledHeight = H * zoom;
            const rasterSrc = page?.rendered_page?.base64 ? `data:image/${String(page.rendered_page?.format || 'png').replace('jpg', 'jpeg')};base64,${page.rendered_page.base64}` : '';
            const hasRaster = Boolean(rasterSrc);
            const maskBoxes = fields.flatMap((field) => {
              const key = `${pageIndex}:${field.id}`;
              const hasLocalEdit = Object.prototype.hasOwnProperty.call(localEdits, key);
              const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
              const resolved = resolveEditValue(field.text, hasLocalEdit, localEdits[key], hasParentEdit, formData[key]);
              const shouldMask = resolved.hasEdit;
              if (!shouldMask) return [];
              return field.chars.length
                ? field.chars.map((char) => getCharGeometry(char, H)).filter((box, index) => String(field.chars[index]?.text ?? '').trim()).map((box) => ({ x: box.x, y: box.top, width: box.width, height: box.height }))
                : [{ x: field.x, y: field.top, width: field.width, height: field.height }];
            });

            return (
              <div className="invoice-page-space" key={pageIndex} style={{ width: scaledWidth, height: scaledHeight }}>
                <div ref={(el) => { if (el) pageRefs.current.set(pageIndex, el); else pageRefs.current.delete(pageIndex); }} className="invoice-page" data-pdf-page-width={W} data-pdf-page-height={H} style={{ width: scaledWidth, height: scaledHeight }}>
                  {hasRaster ? (
                    <EditedPageRaster src={rasterSrc} pageWidth={W} pageHeight={H} maskBoxes={maskBoxes} className="invoice-pdf-raster" style={{ position: 'absolute', left: 0, top: 0, width: scaledWidth, height: scaledHeight, zIndex: 0, pointerEvents: 'none', userSelect: 'none', objectFit: 'fill' }} />
                  ) : renderDrawingItems(page.drawings, W, H)}

                  {!hasRaster && (page.images || []).map((image, imageIndex) => {
                    const p = image?.position || {};
                    const x = number(p.x);
                    const top = H - number(p.y);
                    const width = number(p.width);
                    const height = number(p.height);
                    if (!image?.base64 || width <= 0 || height <= 0) return null;
                    const format = String(image.format || 'png').toLowerCase();
                    const mime = format === 'jpg' || format === 'jpeg' ? 'image/jpeg' : format === 'webp' ? 'image/webp' : format === 'gif' ? 'image/gif' : 'image/png';
                    return <img key={`image-${pageIndex}-${image.id ?? image.xref ?? imageIndex}`} className="invoice-layout-image" src={`data:${mime};base64,${image.base64}`} alt="" draggable={false} style={{ left: x * zoom, top: top * zoom, width: width * zoom, height: height * zoom, zIndex: 1 }} />;
                  })}

                  {fields.map((field) => {
                    const key = `${pageIndex}:${field.id}`;
                    const isEditing = editingKey === key;
                    const hasLocalEdit = Object.prototype.hasOwnProperty.call(localEdits, key);
                    const hasParentEdit = Object.prototype.hasOwnProperty.call(formData, key);
                    const resolved = resolveEditValue(field.text, hasLocalEdit, localEdits[key], hasParentEdit, formData[key]);
                    const hasEdit = resolved.hasEdit;
                    const text = resolved.value;
                    const selectedFont = selectedFonts[key] || null;
                    const presentation = exactPdfFontPresentation(template, field, selectedFont);
                    const fontSize = Math.max(1, presentation.sizePt * zoom);
                    const measuredWidth = Math.max(0, measureTextWidth(text, presentation, fontSize));
                    const fieldWidth = hasEdit ? Math.max(field.width * zoom, Math.ceil(measuredWidth + Math.max(4, zoom * 3))) : Math.max(1, field.width * zoom);
                    const fieldHeight = Math.max(field.height * zoom, 1);

                    return (
                      <React.Fragment key={key}>
                        {isEditing && (
                          <FontPicker
                                  selectedFont={selectedFont}
                            onSelect={(font) => {
                              setSelectedFonts((previous) => ({ ...previous, [key]: font }));
                              if (font?.id === 'matricebits-z2') void exactMatriceBitsFontReady();
                              const currentElement = fieldRefs.current.get(key);
                              if (currentElement) {
                                const offset = textOffsetFromSelection(currentElement);
                                pendingCaret.current = { ...(pendingCaret.current || {}), key, offset, clientX: null, clientY: null };
                              }
                            }}
                            style={{ left: field.x * zoom, top: field.top * zoom - 30 }}
                          />
                        )}
                        <div
                          key={`editor-${key}`}
                        ref={(element) => { if (element) fieldRefs.current.set(key, element); else fieldRefs.current.delete(key); }}
                        className={`invoice-editable-line direct-editable-text ${isEditing ? 'is-editing' : ''} ${hasEdit ? 'has-edit' : ''}`}
                        data-pdf-editor-field="true"
                        data-direct-text-edit="true"
                        contentEditable={isEditing}
                        suppressContentEditableWarning
                        spellCheck={false}
                        onDoubleClick={(event) => beginEdit(pageIndex, field, event)}
                        onInput={(event) => liveEdit(pageIndex, field, event.currentTarget.textContent || '', event.currentTarget)}
                        onBlur={() => { if (editingKey === key) { setEditingKey(null); pendingCaret.current = null; } }}
                        onKeyDown={(event) => {
                          if (event.key === 'Enter') { event.preventDefault(); event.currentTarget.blur(); }
                          if (event.key === 'Escape') { event.preventDefault(); event.currentTarget.textContent = hasEdit ? text : field.text; event.currentTarget.blur(); }
                        }}
                        onPaste={(event) => {
                          event.preventDefault();
                          const plain = event.clipboardData?.getData('text/plain') || '';
                          const selection = event.currentTarget.ownerDocument.getSelection();
                          if (!selection || !selection.rangeCount) return;
                          selection.deleteFromDocument();
                          const range = selection.getRangeAt(0);
                          range.insertNode(event.currentTarget.ownerDocument.createTextNode(plain));
                          range.collapse(false);
                          selection.removeAllRanges(); selection.addRange(range);
                          event.currentTarget.dispatchEvent(new InputEvent('input', { bubbles: true, inputType: 'insertText', data: plain }));
                        }}
                        style={{
                          position: 'absolute', left: field.x * zoom, top: field.top * zoom, width: fieldWidth, minWidth: field.width * zoom, height: fieldHeight, minHeight: field.height * zoom,
                          overflow: 'visible', background: 'transparent', outline: 'none', border: 0,
                          color: hasEdit ? (field.color || '#000000') : 'transparent', WebkitTextFillColor: hasEdit ? (field.color || '#000000') : 'transparent',
                          fontFamily: presentation.family, fontSize, fontWeight: presentation.weight, fontStyle: presentation.style, lineHeight: `${fontSize}px`, letterSpacing: 0,
                          fontKerning: 'normal', fontVariantLigatures: 'normal', fontSynthesis: 'none', textRendering: 'geometricPrecision', whiteSpace: 'pre', direction: 'ltr', unicodeBidi: 'plaintext',
                          padding: 0, margin: 0, boxSizing: 'border-box', transform: 'none', transformOrigin: 'left top', caretColor: isEditing ? '#111827' : 'transparent',
                          cursor: 'text', userSelect: isEditing ? 'text' : 'none', WebkitUserSelect: isEditing ? 'text' : 'none', pointerEvents: 'auto', zIndex: hasEdit || isEditing ? 20 : 5,
                        }}
                        />
                      </React.Fragment>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <div className="invoice-preview-hint">Double-cliquez sur un texte pour le modifier.</div>
    </div>
  );
}
